import { Button, makeStyles, TableContainer } from '@material-ui/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import useApi from '../hooks/useApi';
import ApiService from '../modules/api-service';
import { useAccountDealerId } from '../modules/bid-buy-helpers';
import { BidDTO, IndividualBidDTO, OfferType } from '../modules/generated/api';
import { ListCarDTO } from '../modules/generated/api/api';

import DialogWithClose from './DialogWithClose';
import IndividualBidTable from './IndividualBidTable';

const useStyles = makeStyles((theme) => ({
  bidsTable: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
  },
}));

type DeleteBidDialogProps = {
  bidForDeletion?: BidDTO | (BidDTO & ListCarDTO);
  carsCount?: number;
  onClose: () => void;
  reload?: () => void;
};

const DeleteBidDialog = ({ bidForDeletion, carsCount, onClose, reload }: DeleteBidDialogProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { fetch } = useApi();
  const currentDealerIds = useAccountDealerId();

  const [localBids, setLocalBids] = useState<IndividualBidDTO[]>(bidForDeletion?.bids ?? []);

  const handleDeleteBid = async (bid: IndividualBidDTO) => {
    await fetch(ApiService.bids.bidControllerCancelBid(bidForDeletion!.id as string, bid.user));
    if (reload) reload();
    const updated = localBids.filter((b) => b.user?.id !== bid.user?.id);
    setLocalBids(updated);

    const remainingDeletable = updated.filter((b) => currentDealerIds?.includes(b.dealer?.id ?? ''));
    if (remainingDeletable.length === 0) {
      onClose();
    }
  };

  return (
    <DialogWithClose
      title={t('bids.delete')}
      open={!!bidForDeletion}
      onClose={onClose}
      stopProp
      actionsBottom={
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          color="primary"
          variant="outlined"
        >
          {t('common.close')}
        </Button>
      }
    >
      {bidForDeletion && (
        <>
          <div>
            {`${t('bids.deleteSelectPrompt')} `}
            {bidForDeletion.offerType === OfferType.Package && carsCount !== undefined ? (
              <strong>
                {t('common.package')} {t('common.carsWithCount', { count: carsCount })}.
              </strong>
            ) : (
              bidForDeletion.cars && <strong>{bidForDeletion.cars[0].model}.</strong>
            )}
          </div>
          <div>{t('bids.deleteUserNote')}</div>
          <TableContainer className={classes.bidsTable}>
            <IndividualBidTable
              bids={localBids}
              onDeleteBid={handleDeleteBid}
              isDeletable={(bid) => !!currentDealerIds?.includes(bid.dealer?.id ?? '')}
            />
          </TableContainer>
        </>
      )}
    </DialogWithClose>
  );
};

export default DeleteBidDialog;
